import type { Resource } from 'i18next';
import React from 'react';

import type { FullscreenProps, LocaleResource } from './../../ricos-types/index.js';
import { hasFullscreen } from './../utils.js';
import { i18next } from './../../wix-rich-content-common/index.js';

import { FullscreenProvider } from '../modals/fullscreen/fullscreen-provider.js';
import viewerVitalTranslations from '../translations/vitalTranslations.json.js';
import type { ContentViewerProps } from '../types.js';
import ContentViewer from './ContentViewer.js';

/**
 * The ricos-viewer is facing a few issues with translations in SSR. Currently, there
 * is no cross-platform method to load chunks in SSR. Some TPAs support
 * Loadable, but the editor-elements do not support any lazy loading in SSR.
 * Consequently, there is no general solution until React 18 is universally
 * adopted and Suspense is supported in SSR. To address the SSR translation
 * issue, we use the following approach:
 * 1. Only five translation keys are directly visible to the users. Therefore,
 * we prepare a JSON file with all the translations for these keys (see
 * vitalTranslations).
 * 2. It's 3KB gzipped— not great, not terrible.
 * 3. In CSR, we load the full translations for the requested locale.
 *
 */
const vitalTranslationsWithDefaults = addDefaultsFromEnglish(viewerVitalTranslations);

export const TranslatedContentViewer: React.FC<
  Omit<ContentViewerProps, 't'> & { fullscreenProps?: FullscreenProps }
> = props => {
  const locale = props.locale;

  const [resourcesState, setResourcesState] = React.useState(() => {
    return {
      status: 'READY' as 'READY' | 'LOADING' | 'ERROR',
      loadedLocales: ['en'],
      i18nextInstance: createI18nextInstance({
        activeLocale: locale,
        resources: vitalTranslationsWithDefaults,
      }),
    };
  });

  const { i18nextInstance, loadedLocales } = resourcesState;

  if (locale !== i18nextInstance.language) {
    // This is done outside of the effect because locale changes might not
    // trigger state changes thus the rendering with updated i18nextInstance
    // will not happen. We can add 'locale's copy to state, but then we will be jugling 3 sources of truth for locale
    i18nextInstance.changeLanguage(locale);
  }

  React.useEffect(() => {
    if (loadedLocales.includes(locale) || resourcesState.status !== 'READY') {
      // If load only unloaded locales and only in 'IDLE' status
      // If the status is 'LOADING', but at the end the loaded locale is not in loadedLocales, it will be loaded then
      // If the status is 'ERROR', we won't try again to prevent infinite loop.
      return;
    }

    // The rest is resource loading
    const loadLocaleResource = async () => {
      try {
        setResourcesState(prevState => ({ ...prevState, status: 'LOADING' }));

        const response = await import(
          /* webpackChunkName: "ricos_viewer_messages_[request]" */
          `../translations/locale/messages_${locale}.json`
        );

        const localeResource = response.default;
        i18nextInstance.addResourceBundle(locale, 'translation', localeResource, true, true);
        setResourcesState(prevState => {
          return {
            ...prevState,
            status: 'READY',
            loadedLocales: [...prevState.loadedLocales, locale],
          };
        });
      } catch (err) {
        console.error(`Failed to load locale resource for ${locale}:`, err);

        if (locale !== 'en' && !loadedLocales.includes('en')) {
          try {
            console.warn(`Falling back to English locale for ${locale}`);
            const fallbackResponse = await import(
              /* webpackChunkName: "ricos_viewer_messages_en" */
              `../translations/locale/messages_en.json`
            );

            const fallbackResource = fallbackResponse.default;
            i18nextInstance.addResourceBundle('en', 'translation', fallbackResource, true, true);

            setResourcesState(prevState => {
              return {
                ...prevState,
                status: 'READY',
                loadedLocales: [...prevState.loadedLocales, 'en'],
              };
            });
          } catch (fallbackErr) {
            console.error('Failed to load fallback English locale:', fallbackErr);
            setResourcesState(prevState => {
              return { ...prevState, status: 'ERROR' };
            });
          }
        } else {
          setResourcesState(prevState => {
            return { ...prevState, status: 'ERROR' };
          });
        }
      }
    };

    loadLocaleResource();
  }, [locale, loadedLocales]);

  const t = React.useMemo(() => i18nextInstance.t.bind(i18nextInstance), [i18nextInstance]);

  return hasFullscreen(props.config) ? (
    <FullscreenProvider
      key="RicosFullscreenProvider"
      fullscreenProps={props?.fullscreenProps}
      isMobile={!!props.isMobile}
      content={props.content}
      experiments={props.experiments}
      config={props.config}
      jsdomMode={props.jsdomMode}
      t={t}
    >
      {({ config, onHover }) => (
        <ContentViewer {...props} config={config} t={t} onHover={onHover} />
      )}
    </FullscreenProvider>
  ) : (
    <ContentViewer {...props} t={t} />
  );
};

function createI18nextInstance({
  activeLocale,
  resources,
}: {
  activeLocale: string;
  resources: { [key: string]: LocaleResource };
}) {
  const resourcesWithNs = Object.keys(resources).reduce<Resource>((acc, key) => {
    acc[key] = { translation: resources[key] };
    return acc;
  }, {});

  const instance = i18next.createInstance({
    lng: activeLocale,
    keySeparator: '$',
    interpolation: { escapeValue: false },
    resources: resourcesWithNs,
  });
  instance.init();
  return instance;
}

type StringResources = {
  [locale: string]: { [key: string]: string };
};

function addDefaultsFromEnglish(resources: StringResources) {
  const english = resources.en;

  return Object.keys(resources).reduce<StringResources>((acc, locale) => {
    acc[locale] = { ...english, ...resources[locale] };
    return acc;
  }, {});
}
